import * as React from 'react';
import { RegisterOptions, UseFormReturn, UseFormRegisterReturn, useWatch } from 'react-hook-form';
import { Fragment, useEffect, useMemo, useRef } from 'react';
import { ErrorRender } from '../../containers/ErrorRender';
import { idGenerator } from '../../../helpers/idGenerator';
import { Link } from 'react-router-dom';

export type CheckboxViewProps = {
  //
  checked?: boolean;
  disabled?: boolean;
  elemWrapperClasses: string;
  errorClasses: string;
  formInstance: UseFormReturn;
  globalClasses: string;
  helper?: string;
  helperClass?: string;
  hideError?: boolean;
  id?: string;
  indeterminate?: boolean;
  label?: string | LabelWithLinks[];
  labelClasses?: string;
  lastLabelFragmentClass?: string;
  name: string;
  opaque?: boolean;
  readonly?: boolean;
  rules: RegisterOptions;
  size: 'xlarge' | 'large' | 'small';
  value?: string;
  wrapper?: boolean;
  action?: (value: boolean) => void;
};

export enum LabelTypes {
  'link' = 'link',
  'label' = 'label'
}

export type LabelWithLinks = {
  [key in LabelTypes]?: {
    text: string;
    route?: string;
    openRouteInNewWindow?: boolean;
  };
};

export const CheckboxView = (props: CheckboxViewProps): JSX.Element => {
  const { action } = props;
  const inputID = useMemo(() => props.id ?? idGenerator(props.name), [props.id, props.name]);
  const localRef = useRef<HTMLInputElement | null>(null);
  const { ref: registerRef, ...register }: UseFormRegisterReturn = props.formInstance.register(props.name, props.rules);

  const watchedValue = useWatch({ control: props.formInstance.control, name: props.name });

  useEffect(() => {
    if (typeof props.checked !== 'undefined' && !!watchedValue !== props.checked) {
      const value = props.value && props.checked ? props.value : props.checked;
      props.formInstance.setValue(props.name, value);
    }
    // TODO-FIX-TS-WARNINGS
    // eslint-disable-next-line
  }, [props.checked]);

  useEffect(() => {
    if (localRef.current) {
      (localRef.current as HTMLInputElement).indeterminate = !!props.indeterminate;
    }
  }, [props.indeterminate]);

  const elem = (
    <input
      id={inputID}
      type={'checkbox'}
      className={props.globalClasses}
      disabled={props.disabled}
      readOnly={props.readonly}
      checked={Array.isArray(watchedValue) ? watchedValue.includes(props.value || 'on') : !!watchedValue}
      {...(props.value ? { value: props.value } : {})}
      {...register}
      ref={ref => {
        localRef.current = ref;
        registerRef(localRef.current);
      }}
      onChange={e => {
        register
          .onChange(e)
          .then(() => {
            props.formInstance.trigger(register.name).catch(() => {
              // prevents runtime error
            });
          })
          .catch(() => {
            // prevents runtime error
          });
        // This as an example, is used by the Discover/FC Posts
        // Provides ability to have a handler for when the check state changes
        if (action) {
          action(e.currentTarget.checked);
        }
      }}
    />
  );

  let label;
  if (props.label && typeof props.label === 'string') {
    label = (
      <label className={props.labelClasses} htmlFor={inputID}>
        {props.label}
      </label>
    );
  } else if (props.label) {
    const labelsArr = props.label as LabelWithLinks[];
    const labelCounter = labelsArr.length;
    label = labelsArr.map((fragment, key) => {
      const tag = Object.keys(fragment)[0] as LabelTypes;
      const lastLabelFragmentClass = labelCounter === key + 1 ? props.lastLabelFragmentClass : '';
      const labelClass = `${props.labelClasses} ${lastLabelFragmentClass}`.trim();
      //return <span key={key}>test</span>;
      if (tag === 'label') {
        return (
          <label key={key} htmlFor={inputID} className={labelClass}>
            {fragment['label']?.text}
          </label>
        );
      }
      return (
        <Link
          target={fragment['link']?.openRouteInNewWindow ? '_blank' : ''}
          key={key}
          to={fragment['link']?.route as string}
          className={lastLabelFragmentClass}>
          {fragment['link']?.text}
        </Link>
      );
    });
  } else {
    label = (
      <label className={props.labelClasses} htmlFor={inputID}>
        <span className={'show-for-sr'}>{props.name}</span>
      </label>
    );
  }
  const help = props.helper ? <p className={props.helperClass}>{props.helper}</p> : null;
  const elemWrapper = (
    <div
      className={props.elemWrapperClasses}
      onClick={e => {
        // Stop propagation as this may be nested inside another list / modal which we don't want to also have
        // actions act upon.
        e.stopPropagation();
      }}>
      {elem}
      {label}
    </div>
  );

  const error: JSX.Element = props.hideError ? (
    <></>
  ) : (
    <ErrorRender errors={props.formInstance.formState.errors} name={props.name} className={props.errorClasses} />
  );

  let buffer = (
    <Fragment>
      {elemWrapper}
      {error}
      {help}
    </Fragment>
  );

  if (props.wrapper) {
    buffer = <div className={'input-wrapper'}>{buffer}</div>;
  }

  return <Fragment>{buffer}</Fragment>;
};
